import React from 'react';
import { useLocation } from '@tanstack/react-router';
import { Box, Alert, CircularProgress } from '@mui/material';
import type { LayoutBlock } from './types';
import { GridWithCommands, GridDialogs } from '../grid/components/';
import { whenCommand } from '../grid/commands/utils';
import { useGridPageLogic } from '../grid/hooks';
import { useDeleteEntityMutation } from '../grid/api/gridApi';
import type { RelationPickerTarget } from '../grid/components/dialogs/RelationPickerDialog';
import type { NavigationRequestOptions } from '../grid/types/navigation';

type AvailableChild = {
  id: number;
  key: string;
  name: string;
  contentType: string;
};

type DetailFormGridSectionProps = {
  block: LayoutBlock;
  parentId: string;
  detailFormKey?: string;
  parentGridKey?: string;
  tabsKey?: string;
  mode: 'add' | 'edit';
  availableChildren?: AvailableChild[];
  onRegisterRelationTarget?: (target: RelationPickerTarget) => () => void;
  initialDateRange?: { startDate?: string; endDate?: string };
};

export const DetailFormGridSection = ({
  block,
  parentId,
  detailFormKey,
  parentGridKey,
  tabsKey,
  mode,
  onRegisterRelationTarget,
  initialDateRange,
}: DetailFormGridSectionProps) => {
  const { pathname } = useLocation();

  // State for delete dialog
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isResetFiltersDialogOpen, setIsResetFiltersDialogOpen] = React.useState(false);
  const [deletingRow, setDeletingRow] = React.useState<Record<string, unknown> | null>(null);
  const [deleteEntity, { isLoading: isDeleting }] = useDeleteEntityMutation();

  // Use unified grid logic hook
  const gridLogic = useGridPageLogic({
    nodeKey: block.key, // Use block's GUID key directly
    parentId: parentId, // For data filtering
    detailFormKey: detailFormKey, // For constructing ParentId when saving
    parentGridKey,
    tabsKey,
    enableBatchOperations: true, // Enable save/delete for child grids
    enableNavigationGuard: true, // Warn on unsaved changes
    skip: mode === 'add' || !parentId,
    initialDateRange,
  });

  // Extract navGuard for navigation with unsaved changes protection
  const navGuard = gridLogic.navGuard!;

  const gridMetadata = gridLogic.metadata;
  const metadataLoading = gridLogic.metadataLoading;
  const metadataError = gridLogic.metadataError;
  const parsedColumns = gridLogic.parsedColumns;
  const dataError = gridLogic.dataError;
  const dataLoading = gridLogic.dataLoading;

  // Destructure the needed properties before useCallback to match compiler expectations
  const contentKey = gridMetadata?.contentKey;
  const relationTargetKey = gridMetadata?.contentKey ?? block.key;
  const displayData = gridLogic.displayData;
  const handleAddNewRowWithData = gridLogic.handleAddNewRowWithData;
  const [selectedRows, setSelectedRows] = React.useState<Set<string>>();

  React.useLayoutEffect(() => {
    if (!onRegisterRelationTarget || !relationTargetKey) return undefined;

    return onRegisterRelationTarget({
      gridKey: relationTargetKey,
      targetRows: displayData,
      selectedRows,
      hasUnsavedChanges: gridLogic.hasUnsavedChanges,
      onAddRowWithData: handleAddNewRowWithData,
    });
  }, [
    onRegisterRelationTarget,
    relationTargetKey,
    displayData,
    selectedRows,
    gridLogic.hasUnsavedChanges,
    handleAddNewRowWithData,
  ]);

  // inlineEditable is derived in useGridPageLogic (including edit command isInlineEdit).
  const hasDetailForm = !gridLogic.inlineEditable;

  // Handle row edit - navigate to detail page with the clicked row's ID
  const handleEdit = React.useCallback(
    (row: Record<string, unknown>) => {
      if (hasDetailForm) {
        // Navigate to detail page (detail form is configured)
        const rowId = String(row.Id);
        const basePath = pathname.split('/').slice(0, -1).join('/');
        // Use navGuard.requestNavigation instead of plain navigate
        navGuard.requestNavigation(`${basePath}/${rowId}`, {
          search: {
            gridContentKey: contentKey,
          },
        });
      }
      // No detail form - do nothing, DataGrid handles inline editing
    },
    [hasDetailForm, contentKey, pathname, navGuard]
  );

  // Handle add new row - navigate if detail form, inline if not
  const handleAddNewRow = React.useCallback(() => {
    if (!gridLogic.inlineEditable) {
      // No inline add — navigate to the add page (detail form is configured).
      const basePath = pathname.split('/').slice(0, -1).join('/');
      navGuard.requestNavigation(`${basePath}/add`, {
        search: {
          gridContentKey: contentKey,
          parentEntityId: parentId,
          ...(detailFormKey ? { parentDetailFormKey: detailFormKey } : {}),
        },
      });
    } else {
      // inlineEditable=true means handleAddNewRow in useRowOperations will not no-op.
      gridLogic.handleAddNewRow();
    }
  }, [gridLogic, pathname, navGuard, contentKey, parentId, detailFormKey]);

  // Handle delete - conditional logic based on inline editable
  const handleDelete = React.useCallback(
    (row: Record<string, unknown>) => {
      const isInlineEditable = gridLogic.inlineEditable;

      if (!isInlineEditable) {
        // NON-INLINE EDITABLE: Immediate delete with confirmation
        setDeletingRow(row);
        setIsDeleteDialogOpen(true);
      } else {
        gridLogic.handleInlineRowDelete(row);
      }
    },
    [gridLogic]
  );

  // Delete dialog handlers
  const handleCloseDeleteDialog = React.useCallback(() => {
    setIsDeleteDialogOpen(false);
    setDeletingRow(null);
  }, []);

  const handleConfirmDelete = React.useCallback(async () => {
    if (!deletingRow || !deletingRow.Id || !gridMetadata?.contentKey) {
      return;
    }

    try {
      await deleteEntity({
        id: String(deletingRow.Id),
        nodeKey: gridMetadata.contentKey,
      }).unwrap();

      handleCloseDeleteDialog();
      gridLogic.refetchGridData();
    } catch (error) {
      console.error('Failed to delete entity:', error);
    }
  }, [deletingRow, gridMetadata, deleteEntity, handleCloseDeleteDialog, gridLogic]);

  const handleConfirmResetFilters = React.useCallback(() => {
    gridLogic.gridFiltering.resetAllFilters();
    setIsResetFiltersDialogOpen(false);
  }, [gridLogic]);

  // Refetch function for commands
  const handleRefresh = React.useCallback(() => {
    gridLogic.refetchGridData();
  }, [gridLogic]);

  // Navigation handler for commands
  const handleNavigate = React.useCallback(
    (path: string | number, options?: NavigationRequestOptions) => {
      navGuard.requestNavigation(typeof path === 'number' ? (path as -1) : path, options);
    },
    [navGuard]
  );

  // Can't show child grids in add mode - parent doesn't exist yet
  if (mode === 'add') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">
          Save the parent record first to view and manage related items.
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (metadataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error states
  if (metadataError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load grid metadata: {JSON.stringify(metadataError)}
        </Alert>
      </Box>
    );
  }

  if (dataError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load grid data: {JSON.stringify(dataError)}</Alert>
      </Box>
    );
  }

  if (!gridMetadata) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">No grid metadata available</Alert>
      </Box>
    );
  }

  const overlayLoading =
    (gridLogic.dataFetching && !gridLogic.dataLoading) || gridLogic.isBatchSaving || isDeleting;
  const gridHeight = block.title ? 'calc(50vh - 48px)' : 'calc(50vh)';

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <GridWithCommands
        metadata={gridMetadata}
        columns={parsedColumns}
        commands={gridMetadata?.commands}
        data={gridLogic.displayData}
        gridData={gridLogic.gridData}
        lookupMaps={gridLogic.lookupMapsAsMap}
        rawLookupItems={gridLogic.rawLookupItemsAsMap}
        dropdownBaseFilter={gridLogic.dropdownBaseFilter}
        totalCount={gridLogic.totalCount}
        page={gridLogic.gridFiltering.pageIndex}
        pageSize={gridLogic.gridFiltering.pageSize}
        onPageChange={gridLogic.gridFiltering.handlePageChange}
        onPageSizeChange={gridLogic.gridFiltering.handlePageSizeChange}
        onSortChange={gridLogic.gridFiltering.handleSortChange}
        onFilterChange={gridLogic.gridFiltering.handleFilterChange}
        onDateRangeChange={gridLogic.gridFiltering.handleDateRangeChange}
        onResetFilters={() => setIsResetFiltersDialogOpen(true)}
        filterResetTrigger={gridLogic.gridFiltering.filterResetTrigger}
        initialColumnFilters={gridLogic.initialColumnFiltersForTable}
        columnFiltersSessionKey={gridLogic.columnFiltersSessionKey}
        loading={dataLoading}
        overlayLoading={overlayLoading}
        nodeKey={gridMetadata?.contentKey}
        inlineEditable={gridLogic.inlineEditable}
        onEdit={hasDetailForm ? handleEdit : undefined}
        onDelete={whenCommand(gridMetadata?.commands, 'delete', handleDelete)}
        onDataUpdate={gridLogic.handleDataUpdate}
        modifiedCells={gridLogic.modifiedCells}
        modifiedRows={gridLogic.modifiedRows}
        markedForDeletion={gridLogic.markedForDeletion}
        hasUnsavedChanges={gridLogic.hasUnsavedChanges}
        isBatchSaving={gridLogic.isBatchSaving}
        onRefresh={handleRefresh}
        onSave={gridLogic.handleBatchSave}
        onCancel={gridLogic.handleCancelAll}
        onAddRow={handleAddNewRow}
        onAddRowWithData={gridLogic.handleAddNewRowWithData}
        onSelectionChange={setSelectedRows}
        navigate={handleNavigate}
        currentPath={pathname}
        parentId={parentId}
        showMetadataChips={false}
        shrinkToRowNumbers={false}
        maxHeight={gridHeight}
        searchTerm={gridLogic.gridFiltering.searchTerm}
        onSearchChange={gridLogic.gridFiltering.handleSearchChange}
        validationErrors={gridLogic.validationErrors}
        dateRange={gridLogic.dateRange}
      />

      <GridDialogs
        deleteDialogOpen={isDeleteDialogOpen}
        onCloseDeleteDialog={handleCloseDeleteDialog}
        onConfirmDelete={handleConfirmDelete}
        isDeleting={isDeleting}
        unsavedDialogOpen={navGuard.isUnsavedChangesDialogOpen}
        onCloseUnsaved={navGuard.handleCancelNavigation}
        onDiscardChanges={navGuard.handleDiscardAndNavigate}
        onSaveChanges={navGuard.handleSaveAndNavigate}
        savingChanges={gridLogic.isBatchSaving}
        saveError={navGuard.saveBeforeNavigateError}
        resetFiltersDialogOpen={isResetFiltersDialogOpen}
        onCloseResetFiltersDialog={() => setIsResetFiltersDialogOpen(false)}
        onConfirmResetFilters={handleConfirmResetFilters}
      />
    </Box>
  );
};
